
import React from 'react';
import { KeyContact } from '@/types/lead';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from '@/components/ui/badge';
import { Star, Trash2, Edit2, Mail, Phone, Briefcase } from 'lucide-react';

interface ContactCardProps {
  contact: KeyContact;
  onEdit: (contact: KeyContact) => void;
  onDelete: (contact: KeyContact) => void;
}

const ContactCard: React.FC<ContactCardProps> = ({ contact, onEdit, onDelete }) => {
  return (
    <Card className="group relative hover:shadow-lg transition-all duration-300 border-border hover:border-border/80 animate-fade-in">
      {contact.isPrimary && (
        <Badge 
          variant="outline" 
          className="absolute -top-2 right-3 bg-amber-50/80 border-amber-300 text-amber-800 shadow-sm z-10 font-semibold backdrop-blur-sm text-xs"
        >
          Primary
        </Badge>
      )}
      
      <CardHeader className="pb-3 space-y-1.5 px-4 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1">
            <CardTitle className="text-lg">{contact.name}</CardTitle>
            {contact.position && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-md inline-flex w-fit">
                <Briefcase className="h-3 w-3" />
                <span>{contact.position}</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 px-4 pb-4">
        <div className="space-y-2">
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center gap-2 text-sm text-foreground font-medium hover:text-primary/80 hover:underline underline-offset-4 transition-colors group/link"
            >
              <div className="flex items-center justify-center w-7 h-7 rounded-md bg-primary/5 text-primary group-hover/link:bg-primary/10 transition-colors">
                <Mail className="h-3.5 w-3.5" />
              </div>
              <span className="text-xs">{contact.email}</span>
            </a>
          )}
          
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              className="flex items-center gap-2 text-sm text-foreground font-medium hover:text-primary/80 hover:underline underline-offset-4 transition-colors group/link"
            >
              <div className="flex items-center justify-center w-7 h-7 rounded-md bg-primary/5 text-primary group-hover/link:bg-primary/10 transition-colors">
                <Phone className="h-3.5 w-3.5" />
              </div>
              <span className="text-xs">{contact.phone}</span>
            </a>
          )}

          {contact.notes && contact.notes.includes('LinkedIn Profile:') && (
            <a
              href={contact.notes.match(/LinkedIn Profile: (https?:\/\/[^\s\n]+)/)?.[1] || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-foreground font-medium hover:text-primary/80 hover:underline underline-offset-4 transition-colors group/link"
            >
              <div className="flex items-center justify-center w-7 h-7 rounded-md bg-[#0A66C2]/5 text-[#0A66C2] group-hover/link:bg-[#0A66C2]/10 transition-colors">
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </div>
              <span className="text-xs">View LinkedIn</span>
            </a>
          )}
        </div>

        {contact.notes && (
          <div className="pt-3 mt-3 border-t border-border/50">
            <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed bg-muted/20 p-2 rounded-md">
              {contact.notes}
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-1.5 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(contact)}
            className="h-8 w-8 hover:bg-primary/10 hover:text-primary transition-colors border border-transparent hover:border-border"
          >
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(contact)}
            className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive transition-colors border border-transparent hover:border-border"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContactCard;
